import React from 'react';

// Mockup-only preview page for the four "Quick Action" tiles in the
// authenticated User Profile. Routed at `/profile-quick-action-mockups`
// so the real Profile component stays untouched until you pick a winner.
//
// Each variant replaces the icon-led card with a different visual treatment
// — all icon-free, all using the brand-blue palette where appropriate.

const ACTIONS = [
  { key: 'quote', title: 'Get a Quote', subtitle: 'Upload 3D files for instant pricing', kicker: '01' },
  { key: 'services', title: 'Services', subtitle: 'Design and optimization services', kicker: '02' },
  { key: 'marketplace', title: 'Browse Designs', subtitle: 'Explore the 3D design marketplace', kicker: '03' },
  { key: 'designer', title: 'Become a Designer', subtitle: 'Sell your 3D designs', kicker: '04', accent: 'emerald' },
];

const Wrap = ({ title, subtitle, children, bg = 'bg-slate-50' }) => (
  <section className={`${bg} rounded-2xl border border-slate-200 p-6 sm:p-8`}>
    <div className="mb-5">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
    {children}
  </section>
);

// ---------- Variant A: Numbered Editorial ----------
// Big serif kicker numbers, generous whitespace, hairline divider on hover.
const VariantA = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {ACTIONS.map((a) => (
      <button
        key={a.key}
        className="group text-left p-6 bg-white rounded-xl border border-slate-200 hover:border-[#0007b5] transition relative overflow-hidden"
      >
        <div className="text-[44px] leading-none font-bold text-slate-100 group-hover:text-[#0007b5]/15 transition-colors mb-6 select-none">
          {a.kicker}
        </div>
        <div className="text-base font-semibold text-slate-900">{a.title}</div>
        <div className="text-sm text-slate-500 mt-1 leading-snug">{a.subtitle}</div>
        <div className="absolute bottom-0 left-6 right-6 h-px bg-slate-200 group-hover:bg-[#0007b5] transition-colors" />
      </button>
    ))}
  </div>
);

// ---------- Variant B: Inline Arrow Cards ----------
// Compact, text-led, animated arrow on the right; arrow becomes solid blue
// pill on hover. No iconography in the card body.
const VariantB = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {ACTIONS.map((a) => (
      <button
        key={a.key}
        className="group flex items-center justify-between p-5 bg-white rounded-xl border border-slate-200 hover:border-[#0007b5] hover:bg-[#0007b5]/[0.02] transition text-left"
      >
        <div>
          <div className="text-base font-semibold text-slate-900">{a.title}</div>
          <div className="text-sm text-slate-500 mt-0.5">{a.subtitle}</div>
        </div>
        <span className="ml-4 inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-300 text-slate-500 group-hover:bg-[#0007b5] group-hover:border-[#0007b5] group-hover:text-white transition">
          {/* CSS-only arrow */}
          <span className="text-lg leading-none translate-x-px transition-transform group-hover:translate-x-0.5">→</span>
        </span>
      </button>
    ))}
  </div>
);

// ---------- Variant C: Brand-Blue Solid Buttons ----------
// Bold, marketing-y. Two-line label, blue solid background, white text,
// subtle gradient on hover. Maximum visual weight.
const VariantC = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {ACTIONS.map((a, i) => (
      <button
        key={a.key}
        className={`text-left p-5 rounded-xl text-white shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 ${
          a.accent === 'emerald'
            ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
            : 'bg-gradient-to-br from-[#0007b5] to-[#000690] hover:from-[#000690] hover:to-[#000470]'
        }`}
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-70">
          Action {i + 1}
        </div>
        <div className="mt-2 text-lg font-bold leading-tight">{a.title}</div>
        <div className="text-sm opacity-85 mt-1 leading-snug">{a.subtitle}</div>
        <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold opacity-80">
          Open
          <span className="text-base leading-none">→</span>
        </div>
      </button>
    ))}
  </div>
);

// ---------- Variant D: Underlined Text Links ----------
// Minimalist, almost like a vertical menu. Big serif titles, tiny meta line,
// brand-blue underline animates in on hover. No card chrome at all.
const VariantD = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
    {ACTIONS.map((a) => (
      <button
        key={a.key}
        className="group text-left py-3 border-b border-slate-200 hover:border-[#0007b5] transition-colors"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-2xl font-bold text-slate-900 group-hover:text-[#0007b5] transition-colors">
            {a.title}
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 group-hover:text-[#0007b5] transition-colors">
            {a.kicker}
          </div>
        </div>
        <div className="text-sm text-slate-500 mt-1">{a.subtitle}</div>
        <div className="mt-2 h-px bg-[#0007b5] w-0 group-hover:w-12 transition-all duration-300" />
      </button>
    ))}
  </div>
);

const ProfileQuickActionMockups = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0007b5] font-semibold mb-2">
            Profile · Quick Actions
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            Four icon-free button mockups
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Same four actions, four different visual treatments. Pick your favourite — I'll
            swap it into the live profile and remove the icons everywhere else they're
            referenced.
          </p>
        </header>

        <div className="space-y-10">
          <Wrap
            title="Variant A · Numbered Editorial"
            subtitle="Oversized muted kicker numbers, hairline divider that turns brand-blue on hover. Calm, magazine-style."
          >
            <VariantA />
          </Wrap>

          <Wrap
            title="Variant B · Inline Arrow Cards"
            subtitle="Two-column compact cards with a circular arrow on the right that fills with brand-blue on hover. Functional, dashboard-y."
            bg="bg-slate-50/70"
          >
            <VariantB />
          </Wrap>

          <Wrap
            title="Variant C · Brand-Blue Solid Buttons"
            subtitle="Bold gradient tiles with caps tracking, white text, subtle lift on hover. Designer-only action keeps the emerald accent."
          >
            <VariantC />
          </Wrap>

          <Wrap
            title="Variant D · Underlined Text Links"
            subtitle="Pure typography — big serif-style title, tiny meta on the right, animated brand-blue underline. Most minimalist."
            bg="bg-white"
          >
            <VariantD />
          </Wrap>
        </div>

        <footer className="mt-12 text-sm text-slate-500 border-t border-slate-200 pt-6">
          When you've picked a winner, just say <span className="font-semibold text-slate-700">"Use Variant X"</span> and I'll wire it into <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Profile.js</code>.
        </footer>
      </div>
    </div>
  );
};

export default ProfileQuickActionMockups;
